import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiGrid,
  FiList,
  FiFileText,
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiPlay,
  FiPause,
  FiXCircle,
  FiCalendar
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { procesosService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Procesos = () => {
  const navigate = useNavigate();
  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  const [error, setError] = useState(null);

  // Aplicar debounce al searchQuery
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    loadProcesos();
  }, [activeTab]);

  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const transformProcesoFromAPI = useCallback((proceso) => ({
    id: proceso.id,
    processType: proceso.process_type || '',
    processTypeLabel: getProcessTypeLabel(proceso.process_type),
    productName: proceso.product_name || '',
    operatorName: proceso.operator_name || '',
    processDate: proceso.process_date || '',
    batchNumber: proceso.batch_number || '',
    status: proceso.status || 'PENDING',
    statusLabel: getStatusLabel(proceso.status),
    qualityStatus: proceso.quality_status || 'PENDING',
    qualityStatusLabel: getQualityStatusLabel(proceso.quality_status),
    quantityProduced: proceso.quantity_produced || 0,
    quantityUnits: proceso.quantity_units || '',
    startTime: proceso.start_time || '',
    endTime: proceso.end_time || '',
    durationMinutes: proceso.duration_minutes || 0,
    notes: proceso.notes || '',
    createdAt: proceso.created_at || '',
    updatedAt: proceso.updated_at || ''
  }), []);

  const loadProcesos = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const baseParams = {
        skip: 0,
        limit: 100
      };

      // Filtrar según la pestaña activa
      if (activeTab === 'pendientes') {
        baseParams.status = 'PENDING';
      } else if (activeTab === 'en-progreso') {
        baseParams.status = 'IN_PROGRESS';
      } else if (activeTab === 'completados') {
        baseParams.status = 'COMPLETED';
      } else if (activeTab === 'calidad-pendiente') {
        baseParams.quality_status = 'PENDING';
      }

      const result = await procesosService.getProcesos(baseParams);

      if (result.success) {
        const procesosTransformados = result.data.map(transformProcesoFromAPI);
        setProcesos(procesosTransformados);
      } else {
        setError(result.message || 'Error al cargar procesos');
        setProcesos([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar procesos');
      console.error('Error al cargar procesos:', error);
      setProcesos([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, transformProcesoFromAPI]);

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      loadProcesos();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = {
        skip: 0,
        limit: 100,
        operator_name: query // Buscar por operador o batch
      };

      const result = await procesosService.getProcesos(searchParams);

      if (result.success) {
        const procesosData = result.data.map(transformProcesoFromAPI);
        setProcesos(procesosData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setProcesos([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching procesos:', err);
      setProcesos([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadProcesos, transformProcesoFromAPI]);

  const handleAddNew = () => {
    navigate('/system/procesos/nuevo');
  };

  const handleDownload = () => {
    console.log('Descargar lista de procesos');
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearError = () => {
    setError(null);
    loadProcesos();
  };

  // Calcular totales por pestaña
  const getTotalProcesos = useCallback((tipo) => {
    return procesos.filter(p => {
      if (tipo === 'todos') return true;
      if (tipo === 'pendientes') return p.status === 'PENDING';
      if (tipo === 'en-progreso') return p.status === 'IN_PROGRESS';
      if (tipo === 'completados') return p.status === 'COMPLETED';
      if (tipo === 'calidad-pendiente') return p.qualityStatus === 'PENDING';
      return true;
    }).length;
  }, [procesos]);

  const getThumbnailType = (processType) => {
    switch (processType) {
      case 'ENCAPSULATION':
        return 'blue-dark-gradient';
      case 'TEA_PACKING':
        return 'green-gradient';
      case 'PACKAGING':
        return 'purple-gradient';
      case 'CLEANING':
        return 'orange-gradient';
      case 'RECEIVING':
        return 'teal-gradient';
      case 'RETURNS':
        return 'red-gradient';
      case 'MAINTENANCE':
        return 'yellow-gradient';
      default:
        return 'blue-dark-gradient';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'status-badge status-badge--success';
      case 'IN_PROGRESS':
        return 'status-badge status-badge--info';
      case 'PENDING':
        return 'status-badge status-badge--warning';
      case 'PAUSED':
        return 'status-badge status-badge--secondary';
      case 'CANCELLED':
        return 'status-badge status-badge--danger';
      default:
        return 'status-badge';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <FiCheckCircle />;
      case 'IN_PROGRESS':
        return <FiPlay />;
      case 'PENDING':
        return <FiClock />;
      case 'PAUSED':
        return <FiPause />;
      case 'CANCELLED':
        return <FiXCircle />;
      default:
        return <FiAlertCircle />;
    }
  };

  function getProcessTypeLabel(type) {
    const types = {
      'ENCAPSULATION': 'Encapsulado',
      'TEA_PACKING': 'Empaque de Tés',
      'PACKAGING': 'Empacado',
      'CLEANING': 'Limpieza',
      'RECEIVING': 'Recepción',
      'RETURNS': 'Retornos',
      'MAINTENANCE': 'Mantenimiento'
    };
    return types[type] || type;
  }

  function getStatusLabel(status) {
    const statuses = {
      'PENDING': 'Pendiente',
      'IN_PROGRESS': 'En Progreso',
      'COMPLETED': 'Completado',
      'PAUSED': 'Pausado',
      'CANCELLED': 'Cancelado'
    };
    return statuses[status] || status;
  }

  function getQualityStatusLabel(status) {
    const statuses = {
      'PENDING': 'Pendiente',
      'APPROVED': 'Aprobado',
      'REJECTED': 'Rechazado',
      'REVIEW_REQUIRED': 'Req. Revisión'
    };
    return statuses[status] || status;
  }

  if (isLoading) {
    return <LoadingGrid message="Cargando procesos..." />;
  }

  return (
    <div className="formulaciones-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">REGISTRO DE PROCESOS</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleAddNew}>
            + REGISTRAR PROCESO
          </button>

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="OPERADOR O LOTE"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
            <button className="view-btn">
              ○
            </button>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="filter-tabs-container">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${activeTab === 'todos' ? 'active' : ''}`}
            onClick={() => setActiveTab('todos')}
          >
            <span className="tab-label">Todos</span>
            <span className="tab-count">{getTotalProcesos('todos')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'pendientes' ? 'active' : ''}`}
            onClick={() => setActiveTab('pendientes')}
          >
            <span className="tab-label">Pendientes</span>
            <span className="tab-count">{getTotalProcesos('pendientes')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'en-progreso' ? 'active' : ''}`}
            onClick={() => setActiveTab('en-progreso')}
          >
            <span className="tab-label">En Progreso</span>
            <span className="tab-count">{getTotalProcesos('en-progreso')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'completados' ? 'active' : ''}`}
            onClick={() => setActiveTab('completados')}
          >
            <span className="tab-label">Completados</span>
            <span className="tab-count">{getTotalProcesos('completados')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'calidad-pendiente' ? 'active' : ''}`}
            onClick={() => setActiveTab('calidad-pendiente')}
          >
            <span className="tab-label">Calidad Pendiente</span>
            <span className="tab-count">{getTotalProcesos('calidad-pendiente')}</span>
          </button>
        </div>
      </div>

      {/* Contenido */}
      {procesos.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron procesos' : 'No hay procesos registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron procesos que coincidan con "${searchQuery}"`
              : 'Comienza registrando el primer proceso de producción'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {procesos.map((proceso) => (
            <div
              key={proceso.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/procesos/${proceso.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(proceso.processType)}`}>
                    <div className="formula-icon">
                      <FiPackage size={32} />
                    </div>
                    <div className={getStatusBadgeClass(proceso.status)}>
                      {getStatusIcon(proceso.status)} {proceso.statusLabel}
                    </div>
                  </div>
                  <div className="content-info">
                    <div className="content-title">{proceso.processTypeLabel}</div>
                    <div className="content-subtitle">{proceso.productName}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiCalendar size={12} /> {proceso.processDate}
                      </span>
                      <span className="meta-item">
                        {proceso.operatorName}
                      </span>
                    </div>
                  </div>
                  <div className="content-card__actions">
                    <FiFileText className="card-icon" />
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(proceso.processType)}`}>
                    <FiPackage size={20} />
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{proceso.processTypeLabel} - {proceso.productName}</div>
                    <div className="content-subtitle">Lote: {proceso.batchNumber || 'N/A'}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiCalendar size={12} /> {proceso.processDate}
                      </span>
                      <span className="meta-item">
                        {proceso.operatorName}
                      </span>
                      <span className="meta-item">
                        {getStatusIcon(proceso.status)} {proceso.statusLabel}
                      </span>
                      <span className={`meta-item ${proceso.qualityStatus === 'PENDING' ? 'warning' : ''}`}>
                        Calidad: {proceso.qualityStatusLabel}
                      </span>
                    </div>
                  </div>
                  <div className="content-actions-list">
                    <FiFileText className="card-icon" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Procesos;
